import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { SlugField } from '@components/core-ui/form-elements/SlugField';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { useNameWithSlug } from '@helpers/useNameWithSlug';
import { useSlugAvailability } from '@helpers/useSlugAvailability';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganizationsService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { getApiCallErrorMessage } from 'app/http/getApiCallErrorMessage';
import { createFetchOrganizationSlugAvailabilityQueryFn } from 'app/queries/organizations/createFetchOrganizationSlugAvailabilityQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
};

export const NewOrganizationModal = ({ onCancel }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [queryError, setQueryError] = useState('');

  const { updateMe } = useAuthContext();
  const navigate = useNavigate();

  const { name, slug, setName, setSlug } = useNameWithSlug();

  const {
    status: slugStatus,
    isAvailable: isSlugAvailable,
    isUnverified: isSlugUnverified,
    retry: retrySlugCheck,
  } = useSlugAvailability({
    slug,
    checkAvailability: async (value) => {
      const available = await queryClient.fetchQuery({
        queryKey: organizationsKeys.slugAvailability(value),
        queryFn: createFetchOrganizationSlugAvailabilityQueryFn(call),
        staleTime: 0,
      });
      return !!available?.result;
    },
  });

  const createOrganizationMutation = useMutation({
    mutationFn: () => call(() => OrganizationsService.create({ requestBody: { name, slug } })),
    onMutate: () => {
      setQueryError('');
    },
    onSuccess: async (data) => {
      if (!data.slug) {
        setQueryError('Something went wrong');
        return;
      }
      const me = await updateMe();
      if (!me) {
        onCancel();
        return;
      }
      const newOrg = me.organizations.find((org) => org.slug === data.slug);
      if (newOrg) {
        navigate(routes.dashboard.make(data.slug));
      }
      onCancel();
    },
    onError: (error) => {
      setQueryError(error instanceof ApiCallError ? getApiCallErrorMessage(error) : 'Something went wrong');
    },
  });
  const isSubmitting = createOrganizationMutation.isPending;

  return (
    <ModalWrapper width="400px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create New Organization</ModalTitle>
      <Content>
        <div className="workspaceSettings-inputWrapper">
          <div className="inputContainer">
            <label htmlFor="title">Organization Name</label>
            <Input
              label="Name"
              name="title"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </div>

          <div className="inputContainer">
            <label htmlFor="slug">Organization ID</label>
            <SlugField
              label="Organization ID"
              value={slug}
              status={slugStatus}
              onChange={setSlug}
              onRetry={retrySlugCheck}
            />
          </div>

          {queryError && <div className="workspaceSettings-errorMessage largeText">{queryError}</div>}
        </div>
        <ModalFooter>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button
            disabled={isSubmitting || isSlugUnverified || !isSlugAvailable || !(slug && name)}
            onClick={() => createOrganizationMutation.mutate()}
          >
            Create
          </Button>
        </ModalFooter>
      </Content>
    </ModalWrapper>
  );
};
